/**
 * Maps the live `guardianControlSurface` GraphQL response
 * (`GetGuardianControlSurfaceQuery`, Phase 0 G1b/c) onto the hand-written
 * `MonitoringStatus` / `MonitoringControlRecord` client shape that the whole
 * monitoring presentation layer (`MonitoringControlReader`,
 * `MonitoringRollupSummary`, `MonitoringEvidenceReport`, and every helper in
 * `monitoring-status-provider.ts`) already consumes untouched.
 *
 * Every function here is pure and total — no React, no network, no throwing.
 * See PHASE-0-BUILD-SPEC.md §G1 client rewire + guardian.graphql's
 * `GuardianControlSurface` doc comment for the documented, deliberate shape
 * differences this bridges:
 *   - `triggers`' fields are camelCase server-side vs snake_case on the
 *     client type (`mapControlSurfaceTriggers`).
 *   - `firstConsulted`/`lastConsulted` are `string | null | undefined` from
 *     GraphQL (optional + nullable) but `string | null` on the client type —
 *     collapsed here (undefined -> null).
 *   - `controlCitations` is `string[] | null | undefined` from GraphQL (null
 *     = no GuardianBundleDefinition found yet for this control, distinct
 *     from `[]` = a resolved definition with no citations) but the client
 *     type only has `string[] | undefined`. That null-vs-absent distinction
 *     has no separate rendering today (every consumer treats null/undefined/
 *     [] identically — see monitoring-control-card.tsx's `citations` calc),
 *     so it is intentionally collapsed (null -> undefined) at this boundary.
 *   - `guidanceKind`/`severity`/`activity`/`attestation`/`status` are plain
 *     `NEString` server-side (so the schema stays agnostic of client-only
 *     union types) — cast to the client's narrower unions here. The server
 *     is the trust boundary: guardian.graphql's doc comments guarantee the
 *     value sets (e.g. activity is only ever "active" | "idle" in Phase 0;
 *     status is one of "uncovered"/"nominal"/"watch"/"elevated" — the G2
 *     threshold dispatcher, `guardian-control-status.ts` — server-side).
 *   - `verification`, `statusSentence`, `statusLine`, `displayName` are
 *     omitted entirely (left `undefined`) — H1 (attestation chain
 *     verification) and generated human-readable status sentences are both
 *     out of this slice's scope, and every consumer already treats these
 *     fields as optional. (Not to be confused with `status`, the new G2
 *     coarse attention-level enum this change adds — a different field.)
 */
import type {
  ActionClassScope,
  GuidanceKind,
  MonitoringControlRecord,
  MonitoringStatus,
  MonitoringSummary,
  MonitoringTriggers,
  RuntimeControlStatus,
} from './monitoring-types';

import type {
  GetGuardianControlSurfaceQuery,
  GuardianControlSurfaceRecordFieldsFragment,
  GuardianControlSurfaceSummaryFieldsFragment,
} from '~/graphql/generated/client';

type ControlSurfaceGQL = NonNullable<
  GetGuardianControlSurfaceQuery['guardianControlSurface']
>;

/** The one field this slice must not get wrong: camelCase (GraphQL
 * convention) -> snake_case (the hand-written client shape). Kept as its
 * own small, pure, unit-tested function per the Phase-0 build spec. */
export function mapControlSurfaceTriggers(
  triggers: GuardianControlSurfaceRecordFieldsFragment['triggers']
): MonitoringTriggers {
  return {
    topic_any_of: triggers.topicAnyOf,
    action_class_in: triggers.actionClassIn,
    topic_required_all_of: triggers.topicRequiredAllOf,
    intent_matches: triggers.intentMatches,
    requires_prior_topic: triggers.requiresPriorTopic,
    action_class_scope: (triggers.actionClassScope ??
      null) as ActionClassScope | null,
  };
}

export function mapControlSurfaceRecord(
  record: GuardianControlSurfaceRecordFieldsFragment
): MonitoringControlRecord {
  return {
    controlId: record.controlId,
    runtimeControlId: record.runtimeControlId,
    name: record.name,
    // displayName intentionally omitted — Phase 0 never emits a re-baked
    // display name; the detail page's existing join already falls back to
    // the joined policy control name, then this record's `name`.
    guidanceKind: record.guidanceKind as GuidanceKind,
    severity: record.severity as MonitoringControlRecord['severity'],
    // statusLine / statusSentence (human-readable generated sentences, a
    // DIFFERENT thing from the `status` enum below) intentionally omitted —
    // getStatusSentence() already degrades to a neutral generated sentence
    // when absent.
    activity: record.activity as MonitoringControlRecord['activity'],
    attestation: record.attestation as MonitoringControlRecord['attestation'],
    consults: record.consults,
    evaluations: record.evaluations,
    sessions: record.sessions,
    firstConsulted: record.firstConsulted ?? null,
    lastConsulted: record.lastConsulted ?? null,
    outcomeKinds: (record.outcomeKinds ?? {}) as Record<string, number>,
    status: record.status as RuntimeControlStatus,
    chainAttested: record.chainAttested,
    triggers: mapControlSurfaceTriggers(record.triggers),
    frameworkRef: record.frameworkRef,
    controlCitations: record.controlCitations ?? undefined,
  };
}

function mapControlSurfaceSummary(
  summary: GuardianControlSurfaceSummaryFieldsFragment
): MonitoringSummary {
  return {
    totalControls: summary.totalControls,
    active: summary.active,
    idle: summary.idle,
    attested: summary.attested,
    attestationGaps: summary.attestationGaps,
    totalConsults: summary.totalConsults,
    controlEvaluations: summary.controlEvaluations,
  };
}

/**
 * Maps the full live `guardianControlSurface` response onto `MonitoringStatus`
 * — the exact shape `MonitoringRollupSummary`, `MonitoringControlReader`,
 * `MonitoringEvidenceReport` and `monitoring-status-provider.ts`'s helpers
 * already consume. `verification` is left unset (Phase 0 does zero
 * signature-chain verification — see the module doc comment above).
 */
export function mapControlSurfaceToStatus(
  surface: ControlSurfaceGQL
): MonitoringStatus {
  return {
    policyId: surface.policyId,
    bundleIdentifier: surface.bundleIdentifier,
    bundleVersion: surface.bundleVersion,
    generatedFromWitnessRecords: surface.generatedFromWitnessRecords,
    summary: mapControlSurfaceSummary(surface.summary),
    stateVocabulary: surface.stateVocabulary,
    controls: surface.controls.map(mapControlSurfaceRecord),
  };
}
